import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Grid, IconButton, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, CircularProgress, Tabs, Tab,
  InputAdornment,
  useTheme, useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export const emptyPatientForm = {
  full_name: '', social_name: '', cpf: '', rg: '', birth_date: '', gender: '',
  marital_status: '', profession: '', patient_source: '', patient_source_id: '',
  email: '', phone: '55', phone_secondary: '',
  whatsapp: '55', address_street: '', address_number: '', address_complement: '',
  address_neighborhood: '', address_city: '', address_state: '', address_zip: '',
  blood_type: '', allergies: [] as string[], chronic_conditions: [] as string[],
  medications: [] as string[], medical_notes: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
  insurance_id: '', insurance_plan: '', insurance_number: '', insurance_validity: '',
  guardian_name: '', guardian_cpf: '', guardian_phone: '', guardian_relation: '',
  notes: '',
};

export interface PatientFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` = novo paciente */
  patientToEdit: Record<string, any> | null;
  onSaved?: (info: { mode: 'create' | 'update' }) => void;
  onError?: (message: string) => void;
}

const PatientFormDialog: React.FC<PatientFormDialogProps> = ({
  open,
  onClose,
  patientToEdit,
  onSaved,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState(emptyPatientForm);
  const [formTabValue, setFormTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [patientSources, setPatientSources] = useState<Array<{ id: string; name: string }>>([]);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const cepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchCep = useCallback(async (raw: string) => {
    const cep = raw.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    setCepError('');
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) {
        setCepError('CEP não encontrado');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        address_street: data.logradouro || prev.address_street,
        address_neighborhood: data.bairro || prev.address_neighborhood,
        address_city: data.localidade || prev.address_city,
        address_state: data.uf || prev.address_state,
        address_zip: raw,
      }));
      setFormTabValue(1);
    } catch {
      setCepError('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  }, []);

  const handleCepChange = (value: string) => {
    setCepError('');
    setFormData((prev) => ({ ...prev, address_zip: value }));
    if (cepTimerRef.current) clearTimeout(cepTimerRef.current);
    const digits = value.replace(/\D/g, '');
    if (digits.length === 8) {
      cepTimerRef.current = setTimeout(() => fetchCep(value), 400);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (patientToEdit) {
      setFormData({
        ...emptyPatientForm,
        ...patientToEdit,
        allergies: patientToEdit.allergies || [],
        chronic_conditions: patientToEdit.chronic_conditions || [],
        medications: patientToEdit.medications || [],
      });
    } else {
      setFormData(emptyPatientForm);
    }
    setFormTabValue(0);
  }, [open, patientToEdit]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/patient-sources'));
        const list = res.data?.success !== false ? (res.data?.data ?? []) : [];
        setPatientSources(Array.isArray(list) ? list.map((x: any) => ({ id: x.id, name: x.name })) : []);
      } catch {
        setPatientSources([]);
      }
    })();
  }, [open]);

  const editingId = patientToEdit?.id ?? null;

  const handleSave = async () => {
    if (!formData.full_name.trim()) {
      onError?.('Nome é obrigatório');
      return;
    }
    try {
      setSaving(true);
      const payload: any = { ...formData };
      if (payload.patient_source_id) {
        payload.patient_source = null;
      } else {
        payload.patient_source_id = null;
      }
      if (editingId) {
        await axios.put(getApiUrl(`/clinic/patients/${editingId}`), payload);
        onSaved?.({ mode: 'update' });
      } else {
        await axios.post(getApiUrl('/clinic/patients'), payload);
        onSaved?.({ mode: 'create' });
      }
      onClose();
    } catch (err: any) {
      onError?.(err.response?.data?.error || 'Erro ao salvar paciente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: isMobile
          ? { m: 0, maxHeight: '100%', borderRadius: 0, height: '100%', display: 'flex', flexDirection: 'column' }
          : {},
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        {editingId ? 'Editar Paciente' : 'Novo Paciente'}
        <IconButton onClick={onClose} aria-label="Fechar"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 } }}>
        <Tabs
          value={formTabValue}
          onChange={(_, v) => setFormTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 2,
            minHeight: { xs: 44, sm: 48 },
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minWidth: { xs: 'auto', sm: 90 } },
            '& .Mui-selected': { color: '#14b8a6 !important' },
            '& .MuiTabs-indicator': { bgcolor: '#14b8a6' },
          }}
        >
          <Tab label="Dados Pessoais" />
          <Tab label="Endereço" />
          <Tab label="Saúde" />
          <Tab label="Convênio" />
          <Tab label="Responsável" />
        </Tabs>

        {formTabValue === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nome Completo *" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nome Social" value={formData.social_name} onChange={(e) => setFormData({ ...formData, social_name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="CPF" value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: e.target.value })} size="small" helperText="Opcional — pode informar depois" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="RG" value={formData.rg} onChange={(e) => setFormData({ ...formData, rg: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Data de Nascimento" type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} size="small" InputLabelProps={{ shrink: true }} helperText="Opcional — pode informar depois" /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Gênero</InputLabel>
                <Select value={formData.gender} label="Gênero" onChange={(e) => setFormData({ ...formData, gender: e.target.value })}>
                  <MenuItem value="masculine">Masculino</MenuItem><MenuItem value="feminine">Feminino</MenuItem>
                  <MenuItem value="other">Outro</MenuItem><MenuItem value="prefer_not_say">Prefiro não dizer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Estado Civil</InputLabel>
                <Select value={formData.marital_status} label="Estado Civil" onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}>
                  <MenuItem value="single">Solteiro(a)</MenuItem><MenuItem value="married">Casado(a)</MenuItem>
                  <MenuItem value="divorced">Divorciado(a)</MenuItem><MenuItem value="widowed">Viúvo(a)</MenuItem>
                  <MenuItem value="other">Outro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Profissão" value={formData.profession} onChange={(e) => setFormData({ ...formData, profession: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Telefone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="WhatsApp" value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Origem do paciente</InputLabel>
                <Select
                  label="Origem do paciente"
                  value={formData.patient_source_id || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      patient_source_id: String(e.target.value),
                      patient_source: e.target.value ? '' : formData.patient_source,
                    })
                  }
                >
                  <MenuItem value="">Não informado</MenuItem>
                  {patientSources.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
                Cadastrar opções em Clínica › Origens de pacientes
              </Typography>
              {!formData.patient_source_id && formData.patient_source ? (
                <Typography variant="caption" sx={{ color: '#b45309', display: 'block', mt: 0.5 }}>
                  Origem legado (texto): {formData.patient_source} — selecione uma origem acima para atualizar.
                </Typography>
              ) : null}
            </Grid>
          </Grid>
        )}

        {formTabValue === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Rua" value={formData.address_street} onChange={(e) => setFormData({ ...formData, address_street: e.target.value })} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_street || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Número" value={formData.address_number} onChange={(e) => setFormData({ ...formData, address_number: e.target.value })} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_number || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Complemento" value={formData.address_complement} onChange={(e) => setFormData({ ...formData, address_complement: e.target.value })} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_complement || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Bairro" value={formData.address_neighborhood} onChange={(e) => setFormData({ ...formData, address_neighborhood: e.target.value })} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_neighborhood || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth label="Cidade" value={formData.address_city} onChange={(e) => setFormData({ ...formData, address_city: e.target.value })} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_city || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink={Boolean(String(formData.address_state || '').trim())}>UF</InputLabel>
                <Select value={formData.address_state} label="UF" onChange={(e) => setFormData({ ...formData, address_state: e.target.value })}>
                  {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                    <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CEP"
                value={formData.address_zip}
                onChange={(e) => handleCepChange(e.target.value)}
                size="small"
                placeholder="00000-000"
                error={!!cepError}
                helperText={cepError || (cepLoading ? 'Buscando endereço…' : '')}
                InputLabelProps={{ shrink: Boolean(String(formData.address_zip || '').trim()) }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {cepLoading
                        ? <CircularProgress size={16} />
                        : <SearchIcon fontSize="small" sx={{ color: '#9ca3af', cursor: 'pointer' }} onClick={() => fetchCep(formData.address_zip)} />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        )}

        {formTabValue === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Tipo Sanguíneo</InputLabel>
                <Select value={formData.blood_type} label="Tipo Sanguíneo" onChange={(e) => setFormData({ ...formData, blood_type: e.target.value })}>
                  {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bt => <MenuItem key={bt} value={bt}>{bt}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Alergias (separar por vírgula)" value={(formData.allergies || []).join(', ')} onChange={(e) => setFormData({ ...formData, allergies: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Condições Crônicas (separar por vírgula)" value={(formData.chronic_conditions || []).join(', ')} onChange={(e) => setFormData({ ...formData, chronic_conditions: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Medicamentos em Uso (separar por vírgula)" value={(formData.medications || []).join(', ')} onChange={(e) => setFormData({ ...formData, medications: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Observações Médicas" value={formData.medical_notes} onChange={(e) => setFormData({ ...formData, medical_notes: e.target.value })} size="small" multiline rows={3} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Contato Emergência - Nome" value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Contato Emergência - Telefone" value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Contato Emergência - Parentesco" value={formData.emergency_contact_relation} onChange={(e) => setFormData({ ...formData, emergency_contact_relation: e.target.value })} size="small" /></Grid>
          </Grid>
        )}

        {formTabValue === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Plano/Convênio" value={formData.insurance_plan} onChange={(e) => setFormData({ ...formData, insurance_plan: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nº do Cartão/Matrícula" value={formData.insurance_number} onChange={(e) => setFormData({ ...formData, insurance_number: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Validade" type="date" value={formData.insurance_validity} onChange={(e) => setFormData({ ...formData, insurance_validity: e.target.value })} size="small" InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
        )}

        {formTabValue === 4 && (
          <Grid container spacing={2}>
            <Grid item xs={12}><Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>Preencher apenas para pacientes menores de idade</Typography></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nome do Responsável" value={formData.guardian_name} onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="CPF do Responsável" value={formData.guardian_cpf} onChange={(e) => setFormData({ ...formData, guardian_cpf: e.target.value })} size="small" helperText="Opcional — pode informar depois" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Telefone do Responsável" value={formData.guardian_phone} onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Parentesco" value={formData.guardian_relation} onChange={(e) => setFormData({ ...formData, guardian_relation: e.target.value })} size="small" /></Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexShrink: 0, gap: 1, flexWrap: 'wrap' }}>
        <Button fullWidth={isMobile} onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button fullWidth={isMobile} variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : editingId ? 'Atualizar' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientFormDialog;
